import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import { ReportBadge } from "@dreamhub/designsystem/icons";

/** The table classes both report tables share, so the two cannot drift. */
// The shared table's thead is sticky but paints no background of its own, so
// rows scroll through the header text. The background belongs on the cells
// rather than on the thead because that is what the table hands this class to,
// and it matches the modal's own surface so the header reads as part of it.
export const REPORTS_TABLE_HEADER_CLASSNAME =
  "border-b border-border-border1 border-t-0 bg-surface-elevation1";
export const REPORTS_TABLE_CLASSNAME = "w-full flex-1 min-h-0";

interface ReportsTableModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  titleIcon: JSX.Element;
  title: string;
  /** Sits above the table, for a surface that filters or searches its rows. */
  toolbar?: React.ReactNode;
  /** When set, shown in place of the table. */
  emptyText?: string;
  /** A full empty state shown in place of the table; takes precedence over `emptyText`. */
  emptyState?: React.ReactNode;
  table: React.ReactNode;
  dataTest: string;
}

/**
 * The full-window shell both report tables sit in — the reports list and the
 * schedules list. It owns the window's size, its title block and its empty
 * state; what the table itself holds is the caller's business.
 */
export const ReportsTableModal: React.FC<ReportsTableModalProps> = ({
  isOpen,
  setIsOpen,
  titleIcon,
  title,
  toolbar,
  emptyText,
  emptyState,
  table,
  dataTest
}) => {
  const modalTitle = (
    <div className="flex items-center gap-3">
      <LogoIconContainer
        icon={titleIcon}
        index={-1}
        iconSize="5"
        className="size-8"
      />
      <Text as="span" variant="lgSubtitleH1" className="normal-case">
        {title}
      </Text>
    </div>
  );

  const content = (
    <div className="flex flex-col flex-1 min-h-0">
      {toolbar}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {emptyState ? (
          emptyState
        ) : emptyText ? (
          <div className="flex flex-1 flex-col items-center justify-center p-6">
            <ReportBadge />
            <Text variant="body">{emptyText}</Text>
          </div>
        ) : (
          table
        )}
      </div>
    </div>
  );

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      title={modalTitle}
      content={content}
      contentClassname="w-full flex flex-col gap-0 max-w-[88vw] h-[82vh] outline-none focus:outline-none focus-visible:outline-none"
      // The table runs edge to edge; a toolbar insets itself.
      contentContainerClassname="flex-1 min-h-0 flex flex-col"
      headerClassname="border-0"
      hideDivider={true}
      showCloseButton={true}
      data-test={dataTest}
      // Radix focuses the first focusable child on open, which scrolls a long
      // table to whichever row holds it; the window itself takes the focus.
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        const target = event.currentTarget as HTMLElement | null;
        requestAnimationFrame(() => target?.focus());
      }}
    />
  );
};
